import { Checkbox, Inline, LinkButton, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import isEmpty from 'lodash/isEmpty';
import fromPairs from 'lodash/fromPairs';
import type { CarrierNew } from '@eti/schema-types';
import { useToggle } from '../../../../../common/custom-hooks/toggle';
import Filter from '../Filter';

const noResetButtonStyles = (isOpen: boolean, shouldShowResetButton: boolean) => css`
  padding-bottom: ${!isOpen && !shouldShowResetButton ? '26px' : '0'};
`;

const actionsWrapperStyles = css`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 4px;

  @media ${mediaQueries.large.up} {
    justify-content: flex-start;
    margin-bottom: 8px;
  }
`;

const disabledStyles = css`
  text-decoration: none;

  &,
  &:hover {
    color: ${etiColors.grayScale[10]};
  }
`;

const actionStyles = (isDisabled: boolean) => css`
  display: inline-block;
  font-size: 0.875rem;
  ${isDisabled ? disabledStyles : undefined};
`;

const expansionToggleStyles = css`
  display: block;
  font-size: 0.875rem;
  margin-top: 8px;
  text-align: center;
`;

interface OptionProps {
  checkedOptionValues: Record<string, boolean>;
  handleChange: () => void;
  id: string;
  option: CarrierNew;
}

const getOptionsCheckedValues = (
  options: CarrierNew[],
  selectedOptions?: string[],
): Record<string, boolean> => {
  if (selectedOptions?.length === 0) {
    return {};
  }

  if (!selectedOptions?.length) {
    return fromPairs(options.map((option) => [option.code, true]));
  }

  return fromPairs(selectedOptions?.map((code) => [code, true]));
};

const Option = ({ checkedOptionValues, handleChange, id, option }: OptionProps) => (
  <Inline alignY="top" noWrap spacing={8}>
    <Checkbox
      checked={Boolean(checkedOptionValues[option.code ?? 0])}
      id={id}
      onChange={handleChange}
      value={option.code ?? ''}
    />
    <Checkbox.Label htmlFor={id} title={option.name ?? ''}>
      {option.name}
    </Checkbox.Label>
  </Inline>
);

interface CheckboxFilterProps {
  className?: string;
  isInitiallyExpanded: boolean;
  isToggleable: boolean;
  label: string;
  name: string;
  onFilter: (filterOptions: Record<string, string[] | null>) => void;
  onReset?: () => void;
  options: CarrierNew[];
  selectedValues?: string[];
  shouldShowResetButton: boolean;
  showLessLabel?: string;
  showMoreLabel: string;
  shownOptionsLimit: number;
}

const CheckboxFilter = ({
  className,
  isInitiallyExpanded,
  isToggleable,
  label,
  name,
  onFilter,
  onReset,
  options,
  selectedValues,
  shouldShowResetButton = true,
  showMoreLabel,
  showLessLabel,
  shownOptionsLimit = 6,
}: CheckboxFilterProps) => {
  const { t } = useTranslation();

  const checkedOptionValues = getOptionsCheckedValues(options, selectedValues);
  const { isOpen, handleToggle } = useToggle(false);

  const areAllDeselected = isEmpty(checkedOptionValues);
  const areAllSelected = Object.keys(checkedOptionValues).length === options.length;

  const handleChange = (code: string) => () => {
    checkedOptionValues[code] = !checkedOptionValues[code];

    const selectedOptions = Object.entries(checkedOptionValues)
      .filter((entry) => entry[1])
      .map((entry) => entry[0]);

    if (selectedOptions.length === options.length) {
      onFilter({ [name]: null });
    } else {
      onFilter({ [name]: selectedOptions });
    }
  };

  const handleClearAll = () => {
    onFilter({ [name]: [] });
  };

  const handleSelectAll = () => {
    onFilter({ [name]: null });
  };

  const showLessText = showLessLabel || t('Result.SearchFilters.ShowLess.Button');
  const showMoreText = showMoreLabel || t('Result.SearchFilters.ShowMore.Button');
  const expandOptionsText = isOpen ? showLessText : showMoreText;
  const expandOptionsDataTestId = isOpen
    ? `resultPage-${name}Filter-show-less-button`
    : `resultPage-${name}Filter-show-more-button`;

  return (
    <Filter
      className={cx(className, noResetButtonStyles(isOpen, shouldShowResetButton))}
      isInitiallyExpanded={isInitiallyExpanded}
      isSelected={Boolean(selectedValues)}
      isToggleable={isToggleable}
      label={label}
      name={name}
      onReset={onReset || handleSelectAll}
      shouldShowResetButton={shouldShowResetButton}
    >
      <div className={actionsWrapperStyles}>
        <LinkButton
          className={actionStyles(areAllSelected)}
          data-testid={`resultPage-${name}Filter-select-all-button`}
          onClick={handleSelectAll}
        >
          {t('Result.SearchFilters.SelectAll.Button')}
        </LinkButton>
        <LinkButton
          className={actionStyles(areAllDeselected)}
          data-testid={`resultPage-${name}Filter-deselect-all-button`}
          onClick={handleClearAll}
        >
          {t('Result.SearchFilters.DeselectAll.Button')}
        </LinkButton>
      </div>
      <Stack spacing={8}>
        {options.slice(0, shownOptionsLimit).map((option) => (
          <Option
            checkedOptionValues={checkedOptionValues}
            handleChange={handleChange(option.code as string)}
            id={`${name.toLowerCase()}-${option.code}`}
            key={option.code}
            option={option}
          />
        ))}
        {isOpen && (
          <>
            {options.slice(shownOptionsLimit, options.length).map((option) => (
              <Option
                checkedOptionValues={checkedOptionValues}
                handleChange={handleChange(option.code as string)}
                id={`${name.toLowerCase()}-${option.code}`}
                key={option.code}
                option={option}
              />
            ))}
          </>
        )}
      </Stack>
      {options.length > shownOptionsLimit && (
        <LinkButton
          className={expansionToggleStyles}
          data-testid={expandOptionsDataTestId}
          onClick={handleToggle}
        >
          {expandOptionsText}
        </LinkButton>
      )}
    </Filter>
  );
};

export default CheckboxFilter;
